import { TDeviceType } from '@cian/ui-kit';
import { dealTypeFromJsonQuery, FDealType, getGeoValueByType } from 'shared/common/packages/JsonQuery';
import { IJsonQueryPolygon } from 'shared/common/packages/api-models/common/json_query';
import { trackApplyClick, trackPageView } from 'shared/map-search/actions/search/tracking';
import { isGeoChanged } from 'shared/map-search/actions/search/utils';
import { selectIsDrawingMode } from 'shared/map-search/selectors/drawing';
import { selectParentRegionId } from 'shared/map-search/selectors/parentRegionId';
import { selectQueryString } from 'shared/map-search/selectors/queryString';
import { selectJsonQuery } from 'shared/map-search/selectors/results';
import { selectUser } from 'shared/map-search/selectors/user';
import { EPinsUpdateTrigger, getMapTelemetry } from 'browser/map-search/services/mapTelemetry';
import { IFiltersUpdatedEventDetail } from 'shared/map-search/types/microfrontends';
import { TThunkAction } from 'shared/map-search/types/redux';
import { produceGeoChangeEvent } from 'shared/map-search/utils/events';
import { getExtendedRegion } from 'shared/map-search/utils/region/getExtendedRegion';
import { getDailyrentUrl } from 'shared/map-search/utils/url/getDailyRentUrl';

import { finishDrawing } from '../drawing';
import { fetchNewbuildingPromoPins } from '../newbuildingPromoFeatures';
import { fetchClusters } from '../results';
import { clearSeoUrl } from '../seoUrl';

interface IParams {
  isAwaitingGeoUpdate: React.MutableRefObject<boolean>;
  deviceType: TDeviceType;
  detail: IFiltersUpdatedEventDetail;
}

export function makeSearch({
  isAwaitingGeoUpdate,
  deviceType,
  detail: { jsonQuery },
}: IParams): TThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const user = selectUser(state);
    const regionId = selectParentRegionId(state);
    const queryString = selectQueryString(state);
    const prevJsonQuery = selectJsonQuery(state);
    const isDrawingMode = selectIsDrawingMode(state);

    const { region } = jsonQuery;

    /**
     * @description
     * Сейчас посутка имеет отдельный мкс, поэтому делаем редирект на него
     */
    if (dealTypeFromJsonQuery(jsonQuery) === FDealType.RentDaily) {
      const dailyRentUrl = getDailyrentUrl(window.location.href);

      window.location.assign(dailyRentUrl);

      return;
    }

    dispatch(clearSeoUrl());

    if (isGeoChanged({ prevJsonQuery, nextJsonQuery: jsonQuery })) {
      isAwaitingGeoUpdate.current = true;

      if (region) {
        produceGeoChangeEvent(getExtendedRegion(region.value[0]));
      }
    }

    const prevPolygonFilter = getGeoValueByType<IJsonQueryPolygon>('polygon')(prevJsonQuery)?.[0];
    const nextPolygonFilter = getGeoValueByType<IJsonQueryPolygon>('polygon')(jsonQuery)?.[0];

    /** Если сбросили фильтр через теги, то убираем активность функционала выделения области */
    if (isDrawingMode && prevPolygonFilter && !nextPolygonFilter) {
      dispatch(finishDrawing());
    }

    /** Применение фильтров — второй после жестов по карте источник обновления пинов (SLI 2) */
    getMapTelemetry().startPinsUpdate(EPinsUpdateTrigger.Filters);

    dispatch(fetchNewbuildingPromoPins({ jsonQuery }));
    await dispatch(fetchClusters({ updateBounds: isAwaitingGeoUpdate.current, deviceType, jsonQuery }));
    isAwaitingGeoUpdate.current = false;

    const { mlSearchSessionGuid } = getState();

    trackPageView({ jsonQuery, user, deviceType, regionId, mlSearchSessionGuid });
    trackApplyClick({ jsonQuery, queryString });
  };
}
